import { Activity, memo, Suspense, useCallback, useEffect, useRef } from 'react'

import { cn } from '@/lib/utils'
import { pathnameOf } from '@/lib/shellSearch'
import { routeById } from '@/config/routeManifest'
import type { WorkspaceTab } from '@/stores/app'
import { useTabReload } from '@/stores/tabReload'
import { useActiveTabId } from '@/hooks/useActiveTabId'
import { useAppNavigate } from '@/hooks/useAppNavigate'
import { useAppTabs } from '@/hooks/useAppTabs'
import { TabOwnerProvider } from '@/components/layout/SheetContainerContext'
import { ErrorBoundary } from '@/components/ErrorBoundary'
import { Button } from '@/components/ui/button'
import { ContentPanel } from '@/components/panel/ContentPanel'
import { AppRouteHost } from '@/components/panel/AppRouteHost'
import { OrgHome } from '@/components/OrgHome'
import { TabBar } from '@/components/panel/tabBar/TabBar'
import { emitTabCountCrossings } from '@/lib/tabActivityTelemetry'

export const ContentPanelAreaInner = memo(function ContentPanelAreaInner() {
  const { toTask } = useAppNavigate()
  const { tabs, requestClose } = useAppTabs()
  const reloadNonces = useTabReload((s) => s.nonces)
  const activeId = useActiveTabId()

  // Threshold emit per session transition. The first effect run only seeds
  // the baseline — never emits — so a restore flow that populates tabs in a
  // same-commit layout effect (count 0 -> N) doesn't fire a spurious crossing.
  const prevTabCountRef = useRef(tabs.length)
  const tabCountMeasuredRef = useRef(false)
  useEffect(() => {
    const previous = prevTabCountRef.current
    const current = tabs.length
    prevTabCountRef.current = current
    if (!tabCountMeasuredRef.current) {
      tabCountMeasuredRef.current = true
      return
    }
    if (previous === current) return
    emitTabCountCrossings({
      previous,
      current,
      routes: tabs.map((t) => pathnameOf(t.href)),
    })
  }, [tabs])

  // Overflow dropdown only — the visible tabs are links and navigate on click.
  const handleTabChange = useCallback(
    (id: string) => {
      if (id === activeId) return
      const tab = tabs.find((t) => t.id === id)
      if (!tab) return
      // The tab's stored href restores its own task query; toTask merges the
      // shell keys (agent/conversationId/view) back in from the current URL.
      toTask(tab.href)
    },
    [tabs, activeId, toTask]
  )

  // With no tabs open, Home renders directly in the content area. Home is not
  // a tab — mainRoutes has no `/` route, so AppShellEffects never opens one.
  if (tabs.length === 0) return <OrgHome />
  // The card's top-LEFT corner sits under the leftmost-visible tab
  // position. When the active tab occupies that position, its own
  // `rounded-t-lg` corner serves as the rounded top-left of the
  // combined shape — so the card's own top-left must be FLAT, otherwise
  // we get a doubled rounded notch. When the leftmost tab is INACTIVE
  // (transparent label), the card's top-left is exposed and needs its
  // normal rounded corner.
  //
  // Heuristic: `tabs[0].id === activeId`. This is correct in the
  // typical case (Pass B in useTabOverflow visits source order from
  // tabs[0], so visible[0] === tabs[0] almost always). The 1% edge
  // case — tabs[0] alone is wider than the budget and ends up in
  // overflow — would mis-flatten, but that requires a single tab
  // wider than the entire content area, which doesn't happen in
  // practice.
  const isLeftmostActive = tabs[0]?.id === activeId
  return (
    <div className="flex-1 min-h-0 flex flex-col">
      <TabBar
        tabs={tabs}
        activeId={activeId}
        onCloseTab={requestClose}
        onSelectTab={handleTabChange}
      />
      {tabs.map((tab) => {
        const isActive = tab.id === activeId
        // Retention policy, as manifest data: a tab is kept mounted in the
        // background only when its entry carries `keepAlive`. Everything else
        // unmounts on background and remounts on select — cheaper memory, no
        // frozen background subtrees, and it preserves the invariant
        // AppRouteHost depends on (a background tab cannot run effects or
        // navigate). Deleting a `keepAlive` field in routeManifest.ts is the
        // kill switch for one route; the whole policy is this branch.
        //
        // Unmounting is safe for the editors that ride it: their drafts live
        // in useTabDrafts (documents, meeting notes) and re-hydrate on
        // remount, or their state is reconstructed from the URL
        // (QueryResultsRoute).
        const keepAlive = Boolean(routeById(tab.routeId)?.keepAlive)
        if (!keepAlive && !isActive) return null

        const panel = (
          <TabPanel
            key={tab.id}
            tab={tab}
            isActive={isActive}
            isLeftmostActive={isLeftmostActive}
            reloadNonce={reloadNonces[tab.id] ?? 0}
          />
        )

        return keepAlive ? (
          <Activity
            key={tab.id}
            name={tab.id}
            mode={isActive ? 'visible' : 'hidden'}>
            {panel}
          </Activity>
        ) : (
          panel
        )
      })}
    </div>
  )
})

function TabPanel({
  tab,
  isActive,
  isLeftmostActive,
  reloadNonce,
}: {
  tab: WorkspaceTab
  isActive: boolean
  isLeftmostActive: boolean
  reloadNonce: number
}) {
  return (
    <div
      data-slot="tabs-content"
      data-state={isActive ? 'active' : 'inactive'}
      hidden={!isActive}
      role="region"
      aria-label={tab.title}
      className={cn(
        'outline-none',
        // The `hidden` attribute above owns the inactive case;
        // data-state stays for the unit and e2e selectors.
        'data-[state=active]:flex data-[state=active]:flex-1 data-[state=active]:min-h-0',
        'relative bg-tab overflow-hidden',
        'rounded-tr-2xl rounded-bl-2xl rounded-br-2xl',
        isLeftmostActive ? 'rounded-tl-none' : 'rounded-tl-2xl',
        // Hover of the leftmost visible tab also flattens the
        // corner (so the tab's `rounded-t-lg` hover treatment
        // merges cleanly into the card). The TabBar wrapper
        // carries `peer/tabbar` — we use a `peer-has`
        // arbitrary-variant to detect when its visible row's
        // first child is hovered. The measurement row is
        // excluded via `:not([aria-hidden])`.
        'peer-has-[[data-slot=tabs-list]:not([aria-hidden])>:first-child:hover]/tabbar:rounded-tl-none'
      )}>
      <TabOwnerProvider tabId={tab.id}>
        {/* Per-tab containment, and the reason no route needs a boundary of
            its own. One boundary instance per TabPanel, INSIDE the panel card
            and outside the route tree, so a chunk that fails to fetch or a
            component that throws costs exactly this tab: the shelf, the other
            tabs and the chat panel all keep working.

            Suspense sits inside it, not around it, because Suspense only ever
            covers the PENDING state — a rejected lazy import is an error, and
            React caches the rejection, so re-rendering cannot recover. Reload
            is the whole fallback for that reason. */}
        <ErrorBoundary
          fallback={() => (
            <div className="flex flex-1 flex-col items-center justify-center gap-3 p-6 text-center">
              <p className="text-sm text-muted-foreground">
                This tab failed to load.
              </p>
              <Button
                variant="outline"
                size="sm"
                onClick={() => window.location.reload()}>
                Reload
              </Button>
            </div>
          )}>
          <Suspense fallback={<div className="bg-tab h-full flex-1" />}>
            <ContentPanel>
              <AppRouteHost key={reloadNonce} tab={tab} isActive={isActive} />
            </ContentPanel>
          </Suspense>
        </ErrorBoundary>
      </TabOwnerProvider>
    </div>
  )
}
